import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { GivingHistoryCard } from '../../molecules/giving-history-card.tsx/giving-history-card.tsx';
import { SectionContent } from '../../molecules/section-content/section-content.tsx';
import { GivingItemWithCommitment } from '../../utils/giving-history.ts';
import {
  getGivingHistoryTotal,
  getGroupedHistoryItems,
} from './giving-history.logic.ts';

export type GivingHistoryMonthProps =
  | {
      isLoading: true;
      heading?: never;
      givingHistoryItems?: never;
    }
  | {
      isLoading?: false;
      heading: string;
      givingHistoryItems: GivingItemWithCommitment[];
    };

/**
 * Renders group of giving history cards belonging to the same month.
 *
 * Seemed that memoizing the entire component was best since it never needs to rerender
 * and has some expensive calculations based on an array that isn't guaranteed to be memoized.
 */
export const GivingHistoryMonth: React.FC<GivingHistoryMonthProps> = memo(
  ({ heading, givingHistoryItems, isLoading }) => {
    const { t } = useTranslation(['my', 'core']);
    const givingItemDateGroupsFormatted =
      getGroupedHistoryItems(givingHistoryItems);

    return (
      <div
        className="pt-comfy-xl"
        style={{
          borderTop: '1px solid var(--color-container-border-neutral-mild)',
        }}
      >
        {isLoading && <SectionContent isLoadingContent isLoadingHeading />}

        {!isLoading && (
          <SectionContent
            heading={heading}
            subheading={t('numbers.currencyCustom', {
              amount: getGivingHistoryTotal(givingHistoryItems),
            })}
          >
            <div className="flex flex-col gap-comfy-xl">
              {givingItemDateGroupsFormatted?.map(
                ({ heading: subHeading, historyItems }) => (
                  <div
                    key={`giving-history-date-group-${subHeading}`}
                    className="flex flex-col gap-comfy-sm"
                  >
                    <h3 className="label-potent label-md md:label-potent md:label-lg label-neutral-mild">
                      {subHeading}
                    </h3>

                    <div className="flex flex-col gap-snug-xl">
                      {historyItems.map((givingItem, index) => (
                        <GivingHistoryCard
                          key={
                            /**
                             * Using index here for cases when there are multiple payments for commitment during a single day.
                             * Since these cards are never filtered/reordered using index shouldn't hurt anything.
                             */
                            givingItem.commitmentId +
                            givingItem.recordDate +
                            index
                          }
                          givingItem={givingItem}
                        />
                      ))}
                    </div>
                  </div>
                ),
              )}
            </div>
          </SectionContent>
        )}
      </div>
    );
  },
  (prev, next) => prev.isLoading === next.isLoading,
);
